import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { Tract } from '../types/api';

export const tractsKeys = {
  all: ['tracts'] as const,
  list: (projectId: string | null, filters: ListFilters) =>
    [...tractsKeys.all, 'list', projectId, filters] as const,
  byId: (projectId: string | null, id: string) =>
    [...tractsKeys.all, 'byId', projectId, id] as const,
};

export interface ListFilters {
  state?: string | undefined;
  county?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useTractsList(filters: ListFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: tractsKeys.list(projectId, filters),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ tracts: Tract[] }>('/tracts', { ...filters });
      return res.tracts;
    },
  });
}

export function useTract(id: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: id ? tractsKeys.byId(projectId, id) : ['tracts', 'none'],
    enabled: !!id && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ tract: Tract }>(`/tracts/${id}`);
      return res.tract;
    },
  });
}

export interface TractInput {
  name: string;
  state: string;
  county: string;
  description?: string | null | undefined;
  township?: string | null | undefined;
  range?: string | null | undefined;
  section?: string | null | undefined;
  block?: string | null | undefined;
  lot?: string | null | undefined;
  abstract?: string | null | undefined;
  survey?: string | null | undefined;
  gross_acres?: number | null | undefined;
  notes?: string | null | undefined;
}

export function useCreateTract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TractInput) => api.post<{ tract: Tract }>('/tracts', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: tractsKeys.all }),
  });
}

export function useUpdateTract(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<TractInput>) => api.patch<{ tract: Tract }>(`/tracts/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: tractsKeys.all }),
  });
}

export function useDeleteTract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/tracts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: tractsKeys.all }),
  });
}
